import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate, parseMarkdown } from "@/lib/utils";

interface MeetingSummaryProps {
  locationId: string;
  organizationId?: string;
}

interface Meeting {
  id: string;
  date: string;
  type: string;
  chatgptSummary?: string;
}

export function MeetingSummary({ locationId, organizationId }: MeetingSummaryProps) {
  // Build API URL with organization parameter for multi-organization support
  const buildApiUrl = () => {
    let url = `/api/meetings?locationId=${locationId}&latest=true`;
    if (organizationId) {
      url += `&organizationId=${organizationId}`;
    }
    return url;
  };

  // This would fetch the latest meeting in production
  const { data: meeting, isLoading } = useQuery<Meeting>({
    queryKey: [buildApiUrl(), organizationId],
  });

  const currentMeeting = meeting && !Array.isArray(meeting) ? meeting : undefined;

  // Map status indicators based on content
  const getStatusIcon = (section: string, content: string) => {
    if (section === "To-Do List" && content.includes("pending")) {
      return "alert-circle-outline";
    }
    return "checkbox-marked-circle-outline";
  };

  const getStatusColor = (section: string, content: string) => {
    if (section === "To-Do List" && content.includes("pending")) {
      return "text-warning";
    }
    return "text-success";
  };

  // Parse the meeting summary into sections
  const parseMeetingSummary = (): Array<{title: string, content: string}> => {
    if (!currentMeeting?.chatgptSummary || typeof currentMeeting.chatgptSummary !== 'string') return [];
    
    const sections = currentMeeting.chatgptSummary.split(/#{2,3}\s+/g).filter(Boolean);
    
    return sections.map((section: string) => {
      if (!section || typeof section !== 'string') return { title: '', content: '' };
      const [title, ...contentParts] = section.trim().split("\n");
      const content = contentParts.join("\n").trim();
      return { title: title || '', content: content || '' };
    });
  };

  const meetingSections = parseMeetingSummary();

  return (
    <Card className="p-4">
      <CardHeader className="p-0 pb-4 flex justify-between items-center">
        <CardTitle className="text-lg font-medium">Latest Level 10 Meeting</CardTitle>
        {!isLoading && currentMeeting?.date && (
          <span className="text-xs text-gray-500 dark:text-neutral-400">
            {formatDate(currentMeeting.date)}
          </span>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (
          <div className="space-y-4">
            {meetingSections.length === 0 && <p className="text-sm text-slate-600 dark:text-slate-300">No meeting summary available.</p>}
            {meetingSections.map((section, index) => (
              <div key={index}>
                <h3 className="text-sm font-medium mb-2 flex items-center">
                  <span 
                    className={`mdi mdi-${getStatusIcon(section.title, section.content)} ${getStatusColor(section.title, section.content)} mr-1`}
                  ></span>
                  {section.title}
                </h3>
                <div className="pl-6 text-sm">
                  <p className="text-gray-700 dark:text-neutral-300">{section.content}</p>
                </div>
              </div>
            ))}
            
            <Button variant="ghost" className="mt-2 w-full">
              <span className="mdi mdi-file-document-outline mr-1"></span>
              View Full Meeting Notes
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
